import React, { useContext, useEffect, useState } from 'react';
import {
  Table,
  Button,
  Typography,
  InputNumber,
  message,
  Image,
  Empty,
  Popconfirm,
  Card
} from 'antd';
import {
  PlusOutlined,
  MinusOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { CarritoContext } from '../../../../context/carritoContext';
import { SucursalContext } from '../../../../context/sucursalContext';

const { Title } = Typography;

const CCarritoCliente = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { sucursal } = useContext(SucursalContext);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const {
    carrito,
    loading,
    agregarArticulo,
    restarArticulo,
    eliminarArticulo,
    vaciarCarrito,
    fetchCarrito
  } = useContext(CarritoContext);

  const handleActualizarCantidad = async (accion, articuloId) => {
    try {
      if (accion === 'sumar') {
        await agregarArticulo(articuloId, 1);
      } else {
        await restarArticulo(articuloId);
      }
      // Actualizar el carrito después de la operación
      if (fetchCarrito) {
        await fetchCarrito();
      }
    } catch {
      message.error('Error al actualizar cantidad');
    }
  };

  const handleEliminarArticulo = (articuloId) => {
    eliminarArticulo(carrito.car_id, articuloId);
  };

  const handleVaciarCarrito = () => {
    vaciarCarrito(carrito.car_id);
  };

  const getPrecioFinal = (item) => Number(item.precio_final || item.asu_precioventa || 0);

  // Función auxiliar para calcular el subtotal de un item
  const calcularSubtotal = (item) => {
    const tipoUnidadNombre = item.tipo_unidad_nombre || 'Entero';
    const esFraccionado = tipoUnidadNombre.toLowerCase() === 'fraccionado' && item.contenido_neto;
    const contenidoNeto = item.contenido_neto ? parseFloat(item.contenido_neto) : null;
    const unidadMedida = (item.ums_nombre || '').toLowerCase().trim();
    
    const esGramos = esFraccionado && (
      unidadMedida.includes('gramo') || 
      unidadMedida === 'g' || 
      unidadMedida === 'gr' ||
      unidadMedida === 'gramos' ||
      (!unidadMedida && contenidoNeto && contenidoNeto >= 100)
    ) && !unidadMedida.includes('kilo');
    
    const esKilos = esFraccionado && (
      unidadMedida.includes('kilo') || 
      unidadMedida === 'kg' ||
      unidadMedida === 'kilogramo' ||
      unidadMedida === 'kilogramos' ||
      (!unidadMedida && contenidoNeto && contenidoNeto < 10 && contenidoNeto > 0)
    );
    
    const precioFinal = getPrecioFinal(item);
    const cantidadNum = parseFloat(item.dec_cantidad) || 0;
    
    if (esFraccionado && (esGramos || esKilos)) {
      // El precio unitario es por kilo
      // Convertir la cantidad a kilos para el cálculo (igual que en la visualización)
      let cantidadEnKilos;
      
      if (esGramos) {
        // Convertir de gramos a kilos
        cantidadEnKilos = cantidadNum / 1000;
      } else {
        // Ya está en kilos
        cantidadEnKilos = cantidadNum;
      }
      
      // Subtotal = precio por kilo × cantidad en kilos
      return precioFinal * cantidadEnKilos;
    } else {
      // Para artículos enteros, calcular normalmente
      // El precio es por unidad, así que multiplicamos por la cantidad
      return cantidadNum * precioFinal;
    }
  };

  const totalFinal = carrito?.detalles?.reduce(
    (sum, item) => sum + calcularSubtotal(item),
    0
  );

  const cumpleMinimo = totalFinal >= parseFloat(sucursal?.suc_mincompra || 0);

  const tieneItemsEnCarrito = Boolean(carrito?.detalles?.length);
  /** Espacio para la barra fija (total + botones) y mensaje de mínimo; safe-area en iOS */
  const paddingInferiorContenido = tieneItemsEnCarrito
    ? isMobile
      ? 'calc(200px + env(safe-area-inset-bottom, 0px))'
      : 110
    : 24;

  const columns = [
    {
      title: 'Artículo',
      dataIndex: 'aem_nombre',
      key: 'aem_nombre',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Image
            src={record.aem_imagen || 'https://via.placeholder.com/60x60?text=Imagen'}
            width={60}
            height={60}
            style={{ objectFit: 'contain', border: '1px solid #eee', borderRadius: 6 }}
            preview={false}
          />
          <div>{text}</div>
        </div>
      )
    },
    {
      title: 'Precio unitario',
      key: 'precio',
      render: (_, record) => `$${getPrecioFinal(record).toFixed(2)}`
    },
    {
      title: 'Cantidad',
      dataIndex: 'dec_cantidad',
      key: 'cantidad',
      render: (cantidad, record) => {
        const tipoUnidadNombre = record.tipo_unidad_nombre || 'Entero';
        const esFraccionado = tipoUnidadNombre.toLowerCase() === 'fraccionado' && record.contenido_neto;
        const contenidoNeto = record.contenido_neto ? parseFloat(record.contenido_neto) : null;
        const cantidadMinima = esFraccionado ? contenidoNeto : 1;
        
        // Obtener unidad de medida para convertir gramos a kilos
        const unidadMedida = (record.ums_nombre || '').toLowerCase().trim();
        
        // Determinar si es gramos o kilos basándose en la unidad de medida
        // Si no hay unidad de medida, inferir por el valor de contenido_neto:
        // - Si contenido_neto >= 100, probablemente está en gramos
        // - Si contenido_neto < 10, probablemente está en kilos
        const esGramos = esFraccionado && (
          unidadMedida.includes('gramo') || 
          unidadMedida === 'g' || 
          unidadMedida === 'gr' ||
          unidadMedida === 'gramos' ||
          (!unidadMedida && contenidoNeto && contenidoNeto >= 100)
        ) && !unidadMedida.includes('kilo');
        
        const esKilos = esFraccionado && (
          unidadMedida.includes('kilo') || 
          unidadMedida === 'kg' ||
          unidadMedida === 'kilogramo' ||
          unidadMedida === 'kilogramos' ||
          (!unidadMedida && contenidoNeto && contenidoNeto < 10 && contenidoNeto > 0)
        );
        
        // Convertir cantidad a kilos si es necesario
        // IMPORTANTE: La cantidad en dec_cantidad ya está en la unidad base (gramos o kilos)
        // según el contenido_neto del artículo
        const convertirAKilos = (cant) => {
          if (esFraccionado) {
            if (esGramos) {
              // Si la unidad es gramos: dec_cantidad está en gramos, convertir a kilos
              const totalKilos = cant / 1000;
              return parseFloat(totalKilos.toFixed(2));
            } else if (esKilos) {
              // Si la unidad es kilos: dec_cantidad ya está en kilos, mostrar con 1 decimal
              return parseFloat(cant.toFixed(1));
            } else if (!unidadMedida && contenidoNeto) {
              // Si no hay unidad de medida pero hay contenido_neto, inferir:
              // Si la cantidad es >= 100, probablemente está en gramos
              // Si la cantidad es < 10, probablemente está en kilos
              if (cant >= 100) {
                // Probablemente gramos
                const totalKilos = cant / 1000;
                return parseFloat(totalKilos.toFixed(2));
              } else if (cant < 10 && cant > 0) {
                // Probablemente kilos
                return parseFloat(cant.toFixed(1));
              }
            }
          }
          return cant;
        };
        
        const cantidadNum = parseFloat(cantidad) || 0;
        const cantidadMostrar = esFraccionado && (esGramos || esKilos || (!unidadMedida && contenidoNeto))
          ? convertirAKilos(cantidadNum) 
          : cantidadNum;
        
        // Determinar si mostrar el sufijo "kg"
        const mostrarKg = esFraccionado && (esGramos || esKilos || (!unidadMedida && contenidoNeto && (cantidadNum >= 100 || (cantidadNum < 10 && cantidadNum > 0))));
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Button 
              icon={<MinusOutlined />} 
              onClick={() => handleActualizarCantidad('restar', record.dec_articulo)}
              disabled={parseFloat(cantidad) <= cantidadMinima}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <InputNumber
                value={cantidadMostrar}
                readOnly
                style={{
                  textAlign: 'center',
                  width: mostrarKg ? 80 : 60,
                  pointerEvents: 'none',
                  textAlignLast: 'center',
                }}
                precision={mostrarKg ? (esKilos ? 1 : 2) : 0}
              />
              {mostrarKg && (
                <span style={{ fontSize: '0.9rem', color: '#666' }}>kg</span>
              )}
            </div>
            <Button icon={<PlusOutlined />} onClick={() => handleActualizarCantidad('sumar', record.dec_articulo)} />
          </div>
        );
      }
    },
    {
      title: 'Subtotal',
      key: 'subtotal',
      render: (record) => {
        const subtotal = calcularSubtotal(record);
        return `$${subtotal.toFixed(2)}`;
      }
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (record) => (
        <Popconfirm
          title="¿Eliminar este artículo del carrito?"
          onConfirm={() => handleEliminarArticulo(record.dec_articulo)}
          okText="Sí"
          cancelText="No"
        >
          <Button danger icon={<DeleteOutlined />} />
        </Popconfirm>
      )
    }
  ];

  return (
    <div
      style={{
        padding: 12,
        paddingBottom: paddingInferiorContenido,
        minHeight: 'calc(95vh - 80px)'
      }}
    >
      {!carrito || carrito.detalles.length === 0 ? (
        <Empty description="Tu carrito está vacío" />
      ) : (
        <>
          {isMobile && (
            <div style={{ marginBottom: 16 }}>
              <Button
                onClick={() => window.location.href = '/#/home'}
                type="default"
              >
                ← Seguir Comprando
              </Button>
            </div>
          )}

          {isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {carrito.detalles.map((item) => (
                <Card
                  key={item.dec_id}
                  bodyStyle={{ padding: 16 }}
                  style={{
                    borderRadius: 12,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    border: '1px solid #f0f0f0'
                  }}
                >
                  <div style={{ display: 'flex', gap: 12 }}>
                    <Image
                      src={item.aem_imagen || 'https://via.placeholder.com/60x60?text=Imagen'}
                      width={80}
                      height={80}
                      style={{ objectFit: 'contain', borderRadius: 6, border: '1px solid #eee' }}
                      preview={false}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: 16 }}>{item.aem_nombre}</div>
                      <div style={{ color: '#999', fontSize: 14 }}>
                        ${getPrecioFinal(item).toFixed(2)} c/u
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                          marginTop: 12
                        }}
                      >
                        {(() => {
                          const tipoUnidadNombre = item.tipo_unidad_nombre || 'Entero';
                          const esFraccionado = tipoUnidadNombre.toLowerCase() === 'fraccionado' && item.contenido_neto;
                          const contenidoNeto = item.contenido_neto ? parseFloat(item.contenido_neto) : null;
                          const cantidadMinima = esFraccionado ? contenidoNeto : 1;
                          
                          // Obtener unidad de medida para convertir gramos a kilos
                          const unidadMedida = (item.ums_nombre || '').toLowerCase().trim();
                          
                          // Determinar si es gramos o kilos basándose en la unidad de medida
                          // Si no hay unidad de medida, inferir por el valor de contenido_neto:
                          // - Si contenido_neto >= 100, probablemente está en gramos
                          // - Si contenido_neto < 10, probablemente está en kilos
                          const esGramos = esFraccionado && (
                            unidadMedida.includes('gramo') || 
                            unidadMedida === 'g' || 
                            unidadMedida === 'gr' ||
                            unidadMedida === 'gramos' ||
                            (!unidadMedida && contenidoNeto && contenidoNeto >= 100)
                          ) && !unidadMedida.includes('kilo');
                          
                          const esKilos = esFraccionado && (
                            unidadMedida.includes('kilo') || 
                            unidadMedida === 'kg' ||
                            unidadMedida === 'kilogramo' ||
                            unidadMedida === 'kilogramos' ||
                            (!unidadMedida && contenidoNeto && contenidoNeto < 10 && contenidoNeto > 0)
                          );
                          
                          // Convertir cantidad a kilos si es necesario
                          // IMPORTANTE: La cantidad en dec_cantidad ya está en la unidad base (gramos o kilos)
                          // según el contenido_neto del artículo
                          const convertirAKilos = (cant) => {
                            if (esFraccionado) {
                              if (esGramos) {
                                // Si la unidad es gramos: dec_cantidad está en gramos, convertir a kilos
                                const totalKilos = cant / 1000;
                                return parseFloat(totalKilos.toFixed(2));
                              } else if (esKilos) {
                                // Si la unidad es kilos: dec_cantidad ya está en kilos, mostrar con 1 decimal
                                return parseFloat(cant.toFixed(1));
                              } else if (!unidadMedida && contenidoNeto) {
                                // Si no hay unidad de medida pero hay contenido_neto, inferir:
                                // Si la cantidad es >= 100, probablemente está en gramos
                                // Si la cantidad es < 10, probablemente está en kilos
                                if (cant >= 100) {
                                  // Probablemente gramos
                                  const totalKilos = cant / 1000;
                                  return parseFloat(totalKilos.toFixed(2));
                                } else if (cant < 10 && cant > 0) {
                                  // Probablemente kilos
                                  return parseFloat(cant.toFixed(1));
                                }
                              }
                            }
                            return cant;
                          };
                          
                          const cantidadNum = parseFloat(item.dec_cantidad) || 0;
                          const cantidadMostrar = esFraccionado && (esGramos || esKilos || (!unidadMedida && contenidoNeto))
                            ? convertirAKilos(cantidadNum) 
                            : cantidadNum;
                          
                          // Determinar si mostrar el sufijo "kg"
                          const mostrarKg = esFraccionado && (esGramos || esKilos || (!unidadMedida && contenidoNeto && (cantidadNum >= 100 || (cantidadNum < 10 && cantidadNum > 0))));
                          
                          return (
                            <>
                              <Button
                                size="small"
                                icon={<MinusOutlined />}
                                onClick={() => handleActualizarCantidad('restar', item.dec_articulo)}
                                disabled={parseFloat(item.dec_cantidad) <= cantidadMinima}
                              />
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <InputNumber
                                  size="small"
                                  value={cantidadMostrar}
                                  readOnly
                                  style={{ 
                                    width: mostrarKg ? 80 : 70, 
                                    textAlign: 'center', 
                                    pointerEvents: 'none',
                                    textAlignLast: 'center',
                                  }}
                                  precision={mostrarKg ? (esKilos ? 1 : 2) : 0}
                                />
                                {mostrarKg && (
                                  <span style={{ fontSize: '0.9rem', color: '#666' }}>kg</span>
                                )}
                              </div>
                              <Button
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={() => handleActualizarCantidad('sumar', item.dec_articulo)}
                              />
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 16,
                      paddingInline: 2
                    }}
                  >
                    <div style={{ fontSize: 15, fontWeight: 600 }}>
                      Subtotal:{' '}
                      <span style={{ fontWeight: 'bold', fontSize: 16 }}>
                        ${calcularSubtotal(item).toFixed(2)}
                      </span>
                    </div>

                    <Popconfirm
                      title="¿Eliminar este artículo?"
                      onConfirm={() => handleEliminarArticulo(item.dec_articulo)}
                      okText="Sí"
                      cancelText="No"
                    >
                      <Button
                        icon={<DeleteOutlined />}
                        danger
                        type="text"
                        style={{
                          fontSize: 20,
                          padding: 4,
                          borderRadius: 6
                        }}
                      />
                    </Popconfirm>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Table
              dataSource={carrito.detalles}
              columns={columns}
              rowKey="dec_id"
              pagination={false}
              loading={loading}
            />
          )}
        </>
      )}

      {carrito && carrito.detalles.length > 0 && (
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            width: '100%',
            backgroundColor: '#fff',
            paddingTop: isMobile ? 12 : 16,
            paddingLeft: isMobile ? 16 : 32,
            paddingRight: isMobile ? 16 : 32,
            paddingBottom: isMobile
              ? 'calc(12px + env(safe-area-inset-bottom, 0px))'
              : 16,
            borderTop: '1px solid #f0f0f0',
            boxShadow: '0 -2px 6px rgba(0,0,0,0.05)',
            zIndex: 1000
          }}
        >
          {isMobile ? (
            <>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 12
                }}
              >
                <span style={{ fontWeight: 'bold', fontSize: 16 }}>Total:</span>
                <span style={{ fontWeight: 'bold', fontSize: 18 }}>
                  ${totalFinal.toFixed(2)}
                </span>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  type="primary"
                  size="large"
                  onClick={() => window.location.href = '/#/finalizar-carrito'}
                  style={{
                    backgroundColor: '#01634C',
                    borderColor: '#01634C',
                    color: '#fff'
                  }}
                  disabled={!cumpleMinimo}
                >
                  Finalizar compra
                </Button>

                <Button
                  danger
                  block
                  size="large"
                  style={{ flex: 1 }}
                  onClick={handleVaciarCarrito}
                >
                  Vaciar
                </Button>
              </div>

              {!cumpleMinimo && (
                <div style={{ marginTop: 8, color: 'red', fontWeight: 500 }}>
                  El monto mínimo de compra es ${parseFloat(sucursal?.suc_mincompra).toFixed(2)}
                </div>
              )}
            </>
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button
                    type="primary"
                    size="middle"
                    onClick={() => window.location.href = '/#/finalizar-carrito'}
                    style={{
                      backgroundColor: '#01634C',
                      borderColor: '#01634C',
                      color: '#fff'
                    }}
                    disabled={!cumpleMinimo}
                  >
                    Finalizar compra
                  </Button>
                  <Button
                    danger
                    size="middle"
                    onClick={handleVaciarCarrito}
                  >
                    Vaciar
                  </Button>
                </div>

                <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                  Total: ${totalFinal.toFixed(2)}
                </div>
              </div>

              {!cumpleMinimo && (
                <div style={{ marginTop: 8, color: 'red', fontWeight: 500 }}>
                  El monto mínimo de compra es ${parseFloat(sucursal?.suc_mincompra).toFixed(2)}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CCarritoCliente;